import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../context/I18nContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Pencil, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";

const EMPTY_PRODUCT = { name_en: "", name_ar: "", description_en: "", description_ar: "", category: "plumbing", price: 0, stock: 0, image_url: "", sku: "", brand: "", featured: false };
const EMPTY_JOB = { title_en: "", title_ar: "", trade: "mason", experience_years: 0, location: "Doha, Qatar", salary_min: 0, salary_max: 0, description_en: "", description_ar: "", open: true };

export default function Admin() {
  const { user, loading } = useAuth();
  const { t } = useI18n();
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [applications, setApplications] = useState([]);

  const [pEditing, setPEditing] = useState(null);
  const [pForm, setPForm] = useState(EMPTY_PRODUCT);
  const [jEditing, setJEditing] = useState(null);
  const [jForm, setJForm] = useState(EMPTY_JOB);

  const safeGet = async (url, setter) => {
    try {
      const { data } = await api.get(url);
      setter(data);
    } catch (e) {
      console.warn("Load failed:", url, e?.response?.status);
    }
  };

  const loadAll = async () => {
    await Promise.all([
      safeGet("/products", setProducts),
      safeGet("/admin/orders", setOrders),
      safeGet("/admin/inquiries", setInquiries),
      safeGet("/jobs?open_only=false", setJobs),
      safeGet("/admin/candidates", setCandidates),
      safeGet("/admin/applications", setApplications),
    ]);
  };

  useEffect(() => { if (user?.role === "admin") loadAll(); /* eslint-disable-next-line */ }, [user]);

  if (loading) return <div className="p-10 text-slate-500">{t("common.loading")}</div>;
  if (!user || user.role !== "admin") return <div className="p-10 text-center text-slate-500" data-testid="admin-forbidden">Forbidden</div>;

  // Products
  const startEditP = (p) => { setPEditing(p.id); setPForm(p); };
  const startNewP = () => { setPEditing("new"); setPForm(EMPTY_PRODUCT); };
  const cancelP = () => { setPEditing(null); setPForm(EMPTY_PRODUCT); };
  const saveProduct = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...pForm, price: parseFloat(pForm.price), stock: parseInt(pForm.stock || 0) };
      if (pEditing === "new") await api.post("/admin/products", payload);
      else await api.put(`/admin/products/${pEditing}`, payload);
      toast.success("Saved");
      cancelP();
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };
  const delProduct = async (id) => {
    if (!window.confirm(t("admin.confirmDelete"))) return;
    await api.delete(`/admin/products/${id}`);
    toast.success("Deleted");
    loadAll();
  };

  // Jobs
  const startEditJ = (j) => { setJEditing(j.id); setJForm(j); };
  const startNewJ = () => { setJEditing("new"); setJForm(EMPTY_JOB); };
  const cancelJ = () => { setJEditing(null); setJForm(EMPTY_JOB); };
  const saveJob = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...jForm, experience_years: parseInt(jForm.experience_years || 0), salary_min: parseFloat(jForm.salary_min || 0), salary_max: parseFloat(jForm.salary_max || 0) };
      if (jEditing === "new") await api.post("/admin/jobs", payload);
      else await api.put(`/admin/jobs/${jEditing}`, payload);
      toast.success("Saved");
      cancelJ();
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };
  const delJob = async (id) => {
    if (!window.confirm(t("admin.confirmDelete"))) return;
    await api.delete(`/admin/jobs/${id}`);
    toast.success("Deleted");
    loadAll();
  };

  const updateOrderStatus = async (id, status) => {
    await api.patch(`/admin/orders/${id}`, { status });
    toast.success(t("admin.orderUpdated"));
    loadAll();
  };

  const updateInquiryStatus = async (id, status) => {
    await api.patch(`/admin/inquiries/${id}`, { status });
    toast.success("Updated");
    loadAll();
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12" data-testid="admin-page">
      <h1 className="font-display font-black text-4xl tracking-tighter mb-8">{t("admin.title")}</h1>

      <Tabs defaultValue="products">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="products" data-testid="tab-products">{t("admin.products")} ({products.length})</TabsTrigger>
          <TabsTrigger value="dropship" data-testid="tab-dropship">{t("dropship.title")}</TabsTrigger>
          <TabsTrigger value="orders" data-testid="tab-orders">{t("admin.orders")} ({orders.length})</TabsTrigger>
          <TabsTrigger value="inquiries" data-testid="tab-inquiries">{t("admin.inquiries")} ({inquiries.length})</TabsTrigger>
          <TabsTrigger value="jobs" data-testid="tab-jobs">{t("admin.jobs")} ({jobs.length})</TabsTrigger>
          <TabsTrigger value="applications" data-testid="tab-applications">{t("admin.applications")} ({applications.length})</TabsTrigger>
          <TabsTrigger value="candidates" data-testid="tab-candidates">{t("admin.candidates")} ({candidates.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="products" className="mt-6">
          <div className="flex justify-between mb-4">
            <div className="label-mono text-slate-500">{products.length} items</div>
            <button onClick={startNewP} className="btn-maroon px-4 py-2 font-bold flex items-center gap-2 text-sm" data-testid="add-product-btn">
              <Plus className="w-4 h-4" /> {t("admin.addProduct")}
            </button>
          </div>

          {pEditing && (
            <form onSubmit={saveProduct} className="border border-slate-200 p-6 mb-6 grid md:grid-cols-2 gap-4 bg-slate-50" data-testid="product-form">
              <In label="SKU" v={pForm.sku} on={(v) => setPForm({ ...pForm, sku: v })} req />
              <In label="Brand" v={pForm.brand || ""} on={(v) => setPForm({ ...pForm, brand: v })} />
              <In label="Name (EN)" v={pForm.name_en} on={(v) => setPForm({ ...pForm, name_en: v })} req />
              <In label="Name (AR)" v={pForm.name_ar} on={(v) => setPForm({ ...pForm, name_ar: v })} req />
              <In label="Desc (EN)" v={pForm.description_en} on={(v) => setPForm({ ...pForm, description_en: v })} req />
              <In label="Desc (AR)" v={pForm.description_ar} on={(v) => setPForm({ ...pForm, description_ar: v })} req />
              <Sel label="Category" v={pForm.category} on={(v) => setPForm({ ...pForm, category: v })} opts={[["plumbing","Plumbing"],["sanitary","Sanitary"],["hardware","Hardware"],["building","Building"]]} />
              <In label="Price (QAR)" type="number" step="0.01" v={pForm.price} on={(v) => setPForm({ ...pForm, price: v })} req />
              <In label="Stock" type="number" v={pForm.stock} on={(v) => setPForm({ ...pForm, stock: v })} />
              <In label="Image URL" v={pForm.image_url} on={(v) => setPForm({ ...pForm, image_url: v })} req full />
              <label className="flex items-center gap-2"><input type="checkbox" checked={!!pForm.featured} onChange={(e) => setPForm({ ...pForm, featured: e.target.checked })} /><span className="text-sm font-bold">Featured</span></label>
              <div className="md:col-span-2 flex gap-3">
                <button type="submit" className="btn-maroon px-6 py-2 font-bold" data-testid="save-product-btn">{t("admin.save")}</button>
                <button type="button" onClick={cancelP} className="border border-slate-300 px-6 py-2 font-bold">{t("admin.cancel")}</button>
              </div>
            </form>
          )}

          <div className="border border-slate-200 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200 label-mono text-slate-500">
                <tr><th className="text-start p-3">SKU</th><th className="text-start p-3">Name</th><th className="text-start p-3">Cat</th><th className="text-end p-3">Price</th><th className="text-end p-3">Stock</th><th className="p-3"></th></tr>
              </thead>
              <tbody>
                {products.map((p) => (
                  <tr key={p.id} className="border-b border-slate-100">
                    <td className="p-3 font-mono">{p.sku}</td>
                    <td className="p-3 font-bold">{p.name_en}</td>
                    <td className="p-3 capitalize">{p.category}</td>
                    <td className="p-3 text-end">{p.price.toFixed(2)}</td>
                    <td className="p-3 text-end">{p.stock}</td>
                    <td className="p-3 flex gap-2">
                      <button onClick={() => startEditP(p)} className="p-2 hover:bg-slate-100" data-testid={`edit-${p.sku}`}><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => delProduct(p.id)} className="p-2 hover:bg-red-50 text-red-600" data-testid={`del-${p.sku}`}><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="dropship" className="mt-6">
          <DropshipPanel onDone={loadAll} />
        </TabsContent>

        <TabsContent value="orders" className="mt-6 space-y-3">
          {orders.length === 0 && <div className="text-slate-500 text-center py-10">No orders yet.</div>}
          {orders.map((o) => (
            <div key={o.id} className="border border-slate-200 p-5" data-testid={`admin-order-${o.id}`}>
              <div className="flex flex-wrap justify-between gap-4">
                <div>
                  <div className="label-mono text-slate-500">#{o.id.slice(0,8)} · {o.user_email}</div>
                  <div className="font-bold mt-1">{new Date(o.created_at).toLocaleString()}</div>
                  <div className="text-xs text-slate-500 mt-1">{o.shipping?.full_name} · {o.shipping?.phone} · {o.shipping?.city}</div>
                </div>
                <div className="text-end">
                  <div className="font-display font-black text-xl text-[#8D1B3D]">{o.total.toFixed(2)} {t("common.currency")}</div>
                  <div className="text-xs uppercase mt-1">{o.payment_method} · {o.payment_status}</div>
                </div>
              </div>
              <div className="mt-3 flex gap-2 flex-wrap">
                {["pending","confirmed","shipped","delivered","cancelled"].map((s) => (
                  <button key={s} onClick={() => updateOrderStatus(o.id, s)} className={`label-mono px-3 py-1 border ${o.status === s ? "bg-slate-900 text-white border-slate-900" : "border-slate-300 hover:border-slate-900"}`} data-testid={`status-${o.id}-${s}`}>{s}</button>
                ))}
              </div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="inquiries" className="mt-6 space-y-3">
          {inquiries.length === 0 && <div className="text-slate-500 text-center py-10">No inquiries yet.</div>}
          {inquiries.map((i) => (
            <div key={i.id} className="border border-slate-200 p-5" data-testid={`inq-${i.id}`}>
              <div className="flex flex-wrap justify-between gap-4">
                <div>
                  <div className="label-mono text-slate-500">#{i.id.slice(0,8)} · {i.project_type}</div>
                  <div className="font-display font-bold text-lg mt-1">{i.company_name}</div>
                  <div className="text-sm text-slate-600">{i.contact_name} · {i.email} · {i.phone}</div>
                  <div className="text-sm mt-2"><span className="label-mono text-slate-500">Location:</span> {i.location}</div>
                  <div className="text-sm mt-1"><span className="label-mono text-slate-500">Scope:</span> {i.scope}</div>
                  {i.timeline && <div className="text-sm mt-1"><span className="label-mono text-slate-500">Timeline:</span> {i.timeline}</div>}
                  {i.budget_range && <div className="text-sm mt-1"><span className="label-mono text-slate-500">Budget:</span> {i.budget_range}</div>}
                </div>
                <div className="text-end">
                  <div className="text-xs text-slate-500">{new Date(i.created_at).toLocaleString()}</div>
                </div>
              </div>
              <div className="mt-3 flex gap-2 flex-wrap">
                {["new","contacted","quoted","won","lost"].map((s) => (
                  <button key={s} onClick={() => updateInquiryStatus(i.id, s)} className={`label-mono px-3 py-1 border ${i.status === s ? "bg-slate-900 text-white border-slate-900" : "border-slate-300 hover:border-slate-900"}`} data-testid={`inq-status-${i.id}-${s}`}>{s}</button>
                ))}
              </div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="jobs" className="mt-6">
          <div className="flex justify-between mb-4">
            <div className="label-mono text-slate-500">{jobs.length} jobs</div>
            <button onClick={startNewJ} className="btn-maroon px-4 py-2 font-bold flex items-center gap-2 text-sm" data-testid="add-job-btn">
              <Plus className="w-4 h-4" /> {t("admin.addJob")}
            </button>
          </div>
          {jEditing && (
            <form onSubmit={saveJob} className="border border-slate-200 p-6 mb-6 grid md:grid-cols-2 gap-4 bg-slate-50" data-testid="job-form">
              <In label="Title (EN)" v={jForm.title_en} on={(v) => setJForm({ ...jForm, title_en: v })} req />
              <In label="Title (AR)" v={jForm.title_ar} on={(v) => setJForm({ ...jForm, title_ar: v })} req />
              <Sel label="Trade" v={jForm.trade} on={(v) => setJForm({ ...jForm, trade: v })} opts={[["mason","Mason"],["plumber","Plumber"],["electrician","Electrician"],["foreman","Foreman"],["carpenter","Carpenter"],["painter","Painter"],["hvac","HVAC"],["helper","Helper"],["other","Other"]]} />
              <In label="Experience (years)" type="number" v={jForm.experience_years} on={(v) => setJForm({ ...jForm, experience_years: v })} />
              <In label="Location" v={jForm.location} on={(v) => setJForm({ ...jForm, location: v })} req />
              <In label="Salary min" type="number" v={jForm.salary_min} on={(v) => setJForm({ ...jForm, salary_min: v })} />
              <In label="Salary max" type="number" v={jForm.salary_max} on={(v) => setJForm({ ...jForm, salary_max: v })} />
              <In label="Desc (EN)" v={jForm.description_en} on={(v) => setJForm({ ...jForm, description_en: v })} full />
              <In label="Desc (AR)" v={jForm.description_ar} on={(v) => setJForm({ ...jForm, description_ar: v })} full />
              <label className="flex items-center gap-2"><input type="checkbox" checked={!!jForm.open} onChange={(e) => setJForm({ ...jForm, open: e.target.checked })} /><span className="text-sm font-bold">Open</span></label>
              <div className="md:col-span-2 flex gap-3">
                <button type="submit" className="btn-maroon px-6 py-2 font-bold" data-testid="save-job-btn">{t("admin.save")}</button>
                <button type="button" onClick={cancelJ} className="border border-slate-300 px-6 py-2 font-bold">{t("admin.cancel")}</button>
              </div>
            </form>
          )}
          <div className="space-y-3">
            {jobs.map((j) => (
              <div key={j.id} className="border border-slate-200 p-5 flex flex-wrap justify-between gap-4" data-testid={`admin-job-${j.id}`}>
                <div>
                  <div className="label-mono text-slate-500">{j.trade} · {j.location} · {j.open ? "OPEN" : "CLOSED"}</div>
                  <div className="font-display font-bold text-lg mt-1">{j.title_en}</div>
                  <div className="text-sm text-slate-600 mt-1">{j.experience_years}+ yrs · QAR {j.salary_min}-{j.salary_max}</div>
                </div>
                <div className="flex gap-2 self-start">
                  <button onClick={() => startEditJ(j)} className="p-2 hover:bg-slate-100" data-testid={`edit-job-${j.id}`}><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => delJob(j.id)} className="p-2 hover:bg-red-50 text-red-600" data-testid={`del-job-${j.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="applications" className="mt-6 space-y-3">
          {applications.length === 0 && <div className="text-slate-500 text-center py-10">No applications.</div>}
          {applications.map((a) => (
            <div key={a.id} className="border border-slate-200 p-5">
              <div className="label-mono text-slate-500">{a.job_title}</div>
              <div className="font-display font-bold text-lg mt-1">{a.full_name}</div>
              <div className="text-sm text-slate-600">{a.email} · {a.phone} · {a.years_experience} yrs exp</div>
              {a.cover_note && <div className="text-sm mt-2 italic text-slate-700">"{a.cover_note}"</div>}
              <div className="text-xs text-slate-400 mt-2">{new Date(a.created_at).toLocaleString()}</div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="candidates" className="mt-6 space-y-3">
          {candidates.length === 0 && <div className="text-slate-500 text-center py-10">No candidates registered.</div>}
          {candidates.map((c) => (
            <div key={c.id} className="border border-slate-200 p-5">
              <div className="label-mono text-slate-500">{c.trade} · {c.availability}</div>
              <div className="font-display font-bold text-lg mt-1">{c.full_name}</div>
              <div className="text-sm text-slate-600">{c.email} · {c.phone} · {c.years_experience} yrs · {c.nationality || "-"}</div>
              {c.bio && <div className="text-sm mt-2 text-slate-700">{c.bio}</div>}
              <div className="text-xs text-slate-400 mt-2">{new Date(c.created_at).toLocaleString()}</div>
            </div>
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function In({ label, type = "text", step, v, on, req, full }) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
      <div className="label-mono text-slate-500 mb-1.5">{label}</div>
      <input type={type} step={step} value={v} required={req} onChange={(e) => on(e.target.value)} className="w-full border border-slate-300 px-3 py-2.5 outline-none focus:border-[#8D1B3D] text-sm" />
    </label>
  );
}

function Sel({ label, v, on, opts }) {
  return (
    <label className="block">
      <div className="label-mono text-slate-500 mb-1.5">{label}</div>
      <select value={v} onChange={(e) => on(e.target.value)} className="w-full border border-slate-300 px-3 py-2.5 outline-none focus:border-[#8D1B3D] text-sm">
        {opts.map(([val, lab]) => <option key={val} value={val}>{lab}</option>)}
      </select>
    </label>
  );
}

function DropshipPanel({ onDone }) {
  const { t } = useI18n();
  const [form, setForm] = useState({
    source: "aliexpress", source_url: "", source_sku: "",
    sku: "", brand: "", category: "hardware",
    name_en: "", name_ar: "", description_en: "", description_ar: "",
    image_url: "",
    original_price: 0, original_currency: "USD", fx_to_qar: 3.64,
    markup_percent: 35, lead_time_days: 14, stock: 100, featured: false,
  });
  const [loading, setLoading] = useState(false);

  const qarCost = (parseFloat(form.original_price || 0) * parseFloat(form.fx_to_qar || 0)).toFixed(2);
  const salePrice = (qarCost * (1 + parseFloat(form.markup_percent || 0) / 100)).toFixed(2);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/admin/dropship/import", {
        ...form,
        original_price: parseFloat(form.original_price),
        fx_to_qar: parseFloat(form.fx_to_qar),
        markup_percent: parseFloat(form.markup_percent),
        lead_time_days: parseInt(form.lead_time_days),
        stock: parseInt(form.stock),
      });
      toast.success(t("dropship.success"));
      onDone();
      setForm({ ...form, source_url: "", source_sku: "", sku: "", name_en: "", name_ar: "", description_en: "", description_ar: "", image_url: "", original_price: 0 });
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  const SOURCES = [
    ["aliexpress", "AliExpress"], ["alibaba", "Alibaba"], ["alidrop", "AliDrop"],
    ["cj", "CJ Dropshipping"], ["spocket", "Spocket"], ["dsers", "DSers"],
    ["banggood", "Banggood"], ["other", "Other"],
  ];

  return (
    <form onSubmit={submit} className="border border-slate-200 p-6 grid md:grid-cols-3 gap-4 bg-slate-50" data-testid="dropship-form">
      <div className="md:col-span-3 mb-2">
        <div className="label-mono text-slate-500">Manual supplier import</div>
        <h3 className="font-display font-black text-2xl tracking-tight mt-1">{t("dropship.title")}</h3>
        <p className="text-sm text-slate-600 mt-1">{t("dropship.sub")}</p>
      </div>

      <Sel label={t("dropship.source")} v={form.source} on={(v) => setForm({ ...form, source: v })} opts={SOURCES} />
      <In label={t("dropship.url")} v={form.source_url} on={(v) => setForm({ ...form, source_url: v })} req />
      <In label={t("dropship.supplierSku")} v={form.source_sku} on={(v) => setForm({ ...form, source_sku: v })} />

      <In label="Local SKU" v={form.sku} on={(v) => setForm({ ...form, sku: v })} req />
      <In label="Brand" v={form.brand} on={(v) => setForm({ ...form, brand: v })} />
      <Sel label="Category" v={form.category} on={(v) => setForm({ ...form, category: v })} opts={[["plumbing","Plumbing"],["sanitary","Sanitary"],["hardware","Hardware"],["building","Building"]]} />

      <In label="Name (EN)" v={form.name_en} on={(v) => setForm({ ...form, name_en: v })} req />
      <In label="Name (AR)" v={form.name_ar} on={(v) => setForm({ ...form, name_ar: v })} req />
      <In label="Image URL" v={form.image_url} on={(v) => setForm({ ...form, image_url: v })} req />

      <In label="Description (EN)" v={form.description_en} on={(v) => setForm({ ...form, description_en: v })} req />
      <In label="Description (AR)" v={form.description_ar} on={(v) => setForm({ ...form, description_ar: v })} req />
      <In label={t("dropship.stock")} type="number" v={form.stock} on={(v) => setForm({ ...form, stock: v })} />

      <In label={t("dropship.originalPrice")} type="number" step="0.01" v={form.original_price} on={(v) => setForm({ ...form, original_price: v })} req />
      <Sel label={t("dropship.currency")} v={form.original_currency} on={(v) => setForm({ ...form, original_currency: v })} opts={[["USD","USD"],["CNY","CNY"],["EUR","EUR"],["AED","AED"],["SAR","SAR"],["QAR","QAR"]]} />
      <In label={t("dropship.fx")} type="number" step="0.0001" v={form.fx_to_qar} on={(v) => setForm({ ...form, fx_to_qar: v })} req />

      <In label={t("dropship.markup")} type="number" step="0.1" v={form.markup_percent} on={(v) => setForm({ ...form, markup_percent: v })} />
      <In label={t("dropship.leadTime")} type="number" v={form.lead_time_days} on={(v) => setForm({ ...form, lead_time_days: v })} />
      <label className="flex items-center gap-2 mt-6"><input type="checkbox" checked={!!form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} /><span className="text-sm font-bold">Featured</span></label>

      <div className="md:col-span-3 grid sm:grid-cols-2 gap-3 bg-white border border-slate-200 p-4">
        <div>
          <div className="label-mono text-slate-500">{t("dropship.computedQar")}</div>
          <div className="font-display font-black text-2xl mt-1">{qarCost} <span className="text-sm text-slate-500">QAR</span></div>
        </div>
        <div>
          <div className="label-mono text-slate-500">{t("dropship.computedSale")}</div>
          <div className="font-display font-black text-2xl text-[#8D1B3D] mt-1">{salePrice} <span className="text-sm text-slate-500">QAR</span></div>
        </div>
      </div>

      <div className="md:col-span-3">
        <button type="submit" disabled={loading} className="btn-maroon px-8 py-3 font-bold tracking-wide disabled:opacity-60" data-testid="dropship-submit">
          {loading ? t("checkout.processing") : t("dropship.import")}
        </button>
      </div>
    </form>
  );
}
